/**
 * PWA install + update UX.
 *
 * Three responsibilities, kept in one file because they all hinge on the
 * same "are we running standalone?" question and share styling:
 *
 *   1. `<PwaInstallPrompt>` — floating pill bottom-right that fires the
 *      Chrome/Edge/Android `beforeinstallprompt` flow on click.
 *   2. iOS Safari fallback — iOS never fires `beforeinstallprompt`, so we
 *      offer a manual "Zum Home-Bildschirm" Anleitung modal instead.
 *   3. `<PwaUpdateToast>` — surfaces the new-version-available signal that
 *      `main.tsx` dispatches as the `pwa:update-available` CustomEvent.
 *
 * Everything is rendered as a no-op when:
 *   - the build is dev (no SW registered → nothing to install/update),
 *   - we're inside Tauri (already a real app, no PWA install needed), or
 *   - the user is already running in standalone display mode.
 */

import { Download, Plus, RefreshCw, Share, Smartphone, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { isTauri } from "../lib/local-media.js";

/** Spec'd shape of the `beforeinstallprompt` event — DOM TS lib doesn't ship it. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

const DISMISS_KEY_DESKTOP = "pwa-install-dismissed";
const DISMISS_KEY_IOS = "pwa-install-ios-dismissed";

/** True if running as an installed PWA (Android/desktop standalone or iOS home-screen). */
function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  // Safari iOS exposes `navigator.standalone` instead of matchMedia.
  if (typeof navigator !== "undefined" && "standalone" in navigator) {
    return Boolean((navigator as unknown as { standalone?: boolean }).standalone);
  }
  return false;
}

/** True only for iOS Safari (other iOS browsers can't install PWAs at all). */
function isIosSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const iOS =
    /iPhone|iPad|iPod/i.test(ua) && !(window as unknown as { MSStream?: unknown }).MSStream;
  // Chrome/Firefox on iOS still report iPhone/iPad in UA but include CriOS/FxiOS.
  const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
  return iOS && isSafari;
}

/** True if PWA UX should be considered at all (prod web, not Tauri, not standalone). */
function pwaUxEnabled(): boolean {
  if (!import.meta.env.PROD) return false;
  if (isTauri()) return false;
  if (isStandalone()) return false;
  return true;
}

/* ─────────────────── install prompt (Android / desktop) ─────────────────── */

export function PwaInstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem(DISMISS_KEY_DESKTOP) === "1";
  });
  // iOS-Sheet state — surfaced via a separate pill since iOS gets no event.
  const [iosOpen, setIosOpen] = useState(false);
  const [iosDismissed, setIosDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem(DISMISS_KEY_IOS) === "1";
  });
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    if (!pwaUxEnabled()) return;
    const onPrompt = (e: Event) => {
      // Prevent Chrome's mini-infobar so we control the prompt timing.
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setDeferred(null);
      setDismissed(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferred) return;
    setInstalling(true);
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice.outcome === "accepted") {
        setDeferred(null);
      }
    } catch (e) {
      console.error("[pwa] install prompt failed", e);
    } finally {
      setInstalling(false);
    }
  }, [deferred]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    localStorage.setItem(DISMISS_KEY_DESKTOP, "1");
  }, []);

  const handleDismissIos = useCallback(() => {
    setIosDismissed(true);
    localStorage.setItem(DISMISS_KEY_IOS, "1");
  }, []);

  // Don't render anything when we shouldn't (placed AFTER hooks so React
  // doesn't trip over hook-order if standalone changes mid-session).
  if (!pwaUxEnabled()) return null;

  const showDesktop = deferred && !dismissed;
  const showIos = isIosSafari() && !iosDismissed && !deferred;

  if (!showDesktop && !showIos) return null;

  return (
    <>
      {showDesktop && (
        <InstallPill
          label={installing ? "Wird installiert…" : "App installieren"}
          icon={<Download className="size-4" />}
          onClick={() => void handleInstall()}
          onDismiss={handleDismiss}
          busy={installing}
        />
      )}
      {showIos && (
        <>
          <InstallPill
            label="Zum Home-Bildschirm"
            icon={<Smartphone className="size-4" />}
            onClick={() => setIosOpen(true)}
            onDismiss={handleDismissIos}
          />
          {iosOpen && (
            <IosInstallSheet
              onClose={() => setIosOpen(false)}
              onNeverShowAgain={() => {
                handleDismissIos();
                setIosOpen(false);
              }}
            />
          )}
        </>
      )}
    </>
  );
}

/* ─────────────────────── shared "floating pill" UI ─────────────────────── */

function InstallPill({
  label,
  icon,
  onClick,
  onDismiss,
  busy,
}: {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  onDismiss: () => void;
  busy?: boolean;
}) {
  return (
    <div
      className="glass-floating fixed bottom-4 right-4 z-260 flex items-center gap-2 rounded-full border border-white/10 px-3 py-2 pr-2 shadow-2xl"
      style={{
        // Respect iOS notch / Android nav bar.
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)",
        right: "calc(env(safe-area-inset-right, 0px) + 1rem)",
      }}
      role="dialog"
      aria-label="App installieren"
    >
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        className="inline-flex items-center gap-2 rounded-full bg-pitch-lime px-3 py-1.5 text-[13px] font-semibold text-[#1d3700] hover:shadow-glow disabled:opacity-60"
      >
        {icon}
        {label}
      </button>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Hinweis ausblenden"
        className="grid size-7 place-items-center rounded-full text-white/70 hover:bg-white/10"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}

/* ─────────────────────── iOS "Add to Home Screen" sheet ─────────────────── */

function IosInstallSheet({
  onClose,
  onNeverShowAgain,
}: {
  onClose: () => void;
  onNeverShowAgain: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-290 flex items-end justify-center bg-black/60 px-4 backdrop-blur-sm sm:items-center"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="glass-floating w-full max-w-sm rounded-3xl border border-white/10 p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ios-install-title"
        style={{
          // Avoid sitting under the iOS home indicator.
          marginBottom: "env(safe-area-inset-bottom, 0px)",
        }}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h2 id="ios-install-title" className="text-body-md font-semibold leading-tight">
              Coach Organizer installieren
            </h2>
            <p className="mt-1 text-[12px]" style={{ color: "var(--color-on-surface-variant)" }}>
              Als App auf deinem iPhone / iPad — funktioniert offline und ohne Safari-Adressleiste.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="rounded-lg p-1.5 text-white/70 hover:bg-white/10"
          >
            <X className="size-4" />
          </button>
        </div>

        <ol className="mt-2 space-y-3 text-[13px]">
          <IosStep
            number={1}
            icon={<Share className="size-4" />}
            primary="Tippe auf das Teilen-Symbol"
            secondary="Unten in der Safari-Leiste."
          />
          <IosStep
            number={2}
            icon={<Plus className="size-4" />}
            primary={`Wähle "Zum Home-Bildschirm"`}
            secondary="Eventuell musst du nach unten scrollen."
          />
          <IosStep
            number={3}
            icon={<Smartphone className="size-4" />}
            primary={`Tippe auf "Hinzufügen"`}
            secondary="Coach Organizer erscheint dann als App-Icon."
          />
        </ol>

        <div className="mt-4 flex justify-between gap-2">
          <button
            type="button"
            onClick={onNeverShowAgain}
            className="rounded-full px-3 py-1.5 text-[12px] font-medium text-white/60 hover:bg-white/5"
          >
            Nicht mehr anzeigen
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-white/10 px-4 py-1.5 text-[12px] font-semibold text-white hover:bg-white/15"
          >
            Verstanden
          </button>
        </div>
      </div>
    </div>
  );
}

function IosStep({
  number,
  icon,
  primary,
  secondary,
}: {
  number: number;
  icon: React.ReactNode;
  primary: string;
  secondary: string;
}) {
  return (
    <li className="flex gap-3">
      <span
        className="grid size-7 shrink-0 place-items-center rounded-full bg-pitch-lime/15 text-[11px] font-semibold text-pitch-lime"
        aria-hidden
      >
        {number}
      </span>
      <span className="flex-1">
        <span className="flex items-center gap-1.5 font-semibold">
          {primary}
          <span className="text-pitch-lime/80">{icon}</span>
        </span>
        <span className="block text-[12px]" style={{ color: "var(--color-on-surface-variant)" }}>
          {secondary}
        </span>
      </span>
    </li>
  );
}

/* ─────────────────────────── update toast ──────────────────────────────── */

export function PwaUpdateToast() {
  const [visible, setVisible] = useState(false);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    if (!import.meta.env.PROD || isTauri()) return;
    const onAvailable = () => setVisible(true);
    window.addEventListener("pwa:update-available", onAvailable);
    return () => window.removeEventListener("pwa:update-available", onAvailable);
  }, []);

  const handleReload = useCallback(() => {
    setReloading(true);
    const updater = (
      window as unknown as {
        __updateSW__?: (reload?: boolean) => Promise<void>;
      }
    ).__updateSW__;
    if (updater) {
      // `true` instructs the registered SW to skip-waiting and reload.
      void updater(true);
    } else {
      // Fallback: hard reload if for some reason the updater wasn't exposed.
      window.location.reload();
    }
  }, []);

  if (!visible) return null;

  return (
    <div
      className="glass-floating fixed bottom-4 left-1/2 z-270 flex max-w-[calc(100%-2rem)] -translate-x-1/2 items-center gap-3 rounded-full border border-white/10 px-4 py-2.5 shadow-2xl"
      style={{
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)",
      }}
      role="status"
      aria-live="polite"
    >
      <RefreshCw className={`size-4 shrink-0 text-pitch-lime ${reloading ? "animate-spin" : ""}`} />
      <span className="text-[13px] font-medium" style={{ color: "#f4f5f3" }}>
        Neue Version verfügbar
      </span>
      <button
        type="button"
        onClick={handleReload}
        disabled={reloading}
        className="rounded-full bg-pitch-lime px-3 py-1 text-[12px] font-semibold text-[#1d3700] hover:shadow-glow disabled:opacity-60"
      >
        {reloading ? "Lade neu…" : "Aktualisieren"}
      </button>
      <button
        type="button"
        onClick={() => setVisible(false)}
        aria-label="Hinweis ausblenden"
        className="grid size-6 place-items-center rounded-full text-white/60 hover:bg-white/10"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}
